// DateField — shared date input primitive (J-mobile, RULE-144)
//
// Fixes iOS Safari's native date input width issue: the browser renders the
// picker at its intrinsic width (wider than mobile viewport) unless explicitly
// clamped. Applying `w-full min-w-0 box-border` here ensures all consumers
// automatically get the correct mobile behaviour without repeating the classes.
//
// L-01 / L-04 primitive-level fix (Batch L revision):
// Tailwind's `min-w-0` sets `min-width: 0` but iOS Safari additionally enforces
// `-webkit-min-logical-width` on native `<input type="date">` derived from the
// input's intrinsic content (the displayed date string + native calendar glyph).
// That logical-width override IGNORES `min-width: 0` and is the actual reason
// the input renders wider than its flex/grid parent on iPhone Safari (browser
// AND PWA standalone). Two extra utilities are baked into the input here:
//   - `appearance-none` — disables the native widget intrinsic-width override
//     while keeping `type="date"` (the native picker still opens on tap)
//   - `[-webkit-min-logical-width:0]` — explicitly zeroes iOS Safari's
//     logical-width minimum so `width: 100%` is honoured
// Defense-in-depth: see also `src/index.css` which carries equivalent global
// CSS rules for `input[type="date"]` so the fix survives even if a consumer
// overrides classes via the `className` prop.

interface DateFieldProps {
  id?: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  min?: string;
  max?: string;
  disabled?: boolean;
  /** Additional class names applied to the wrapper div */
  className?: string;
}

/**
 * Mobile-safe date input.
 *
 * - `w-full min-w-0 box-border` are baked in — do NOT add them on the call-site.
 * - Pass `label` to get an accessible `<label>` above the input.
 * - Forwards `id` to the input; the label's `htmlFor` matches automatically.
 */
export function DateField({
  id,
  label,
  value,
  onChange,
  required,
  min,
  max,
  disabled,
  className = '',
}: DateFieldProps) {
  return (
    <div className={`min-w-0 flex flex-col gap-1 ${className}`}>
      {label && (
        <label htmlFor={id} className="field-label">
          {label}
          {required && <span className="text-[var(--color-accent)] ml-0.5">*</span>}
        </label>
      )}
      <input
        id={id}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        min={min}
        max={max}
        disabled={disabled}
        // Verified on iPhone Safari 2026-04-25 — see ADR-035. iOS Safari enforces
        // -webkit-min-logical-width on native date inputs; appearance-none +
        // [-webkit-min-logical-width:0] neutralises it.
        className="w-full min-w-0 box-border appearance-none [-webkit-min-logical-width:0] rounded-lg border border-[var(--color-border)] bg-[var(--color-elevated)] text-[var(--color-text-primary)] px-3 py-2 text-body-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={id ? `date-field-${id}` : undefined}
        data-overflow-safe="true"
      />
    </div>
  );
}
